import React, { useState } from "react";
import {
  Title,
  Select,
  GoBack,
  Message,
  PhoneInput,
  InputField,
  ActionButton,
  TextArea,
} from "../../component/Component";
import { codeOk, errorMessage } from "../../../../middlewares/index";
import { useTeamContext } from "../../../../context/team/TeamContext";
import { getOneTeam } from "../../../../middlewares/sysNavigation/SysNavigation";
import { ImageKitUpload } from "../../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { FOLDERS } from "../../../doseeData/utils/Utils";
import { defaultImage } from "../../../../middlewares/libraries/imageKit/ImageKit";

const Edit = () => {
  const { updateTeamState, updateTeam } = useTeamContext();
  const team = getOneTeam();

  const [error, setError] = useState(false);
  const [status, setStatus] = useState(0);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const team_id = team?.team_id;
  const [firstname, setFirstName] = useState(team?.firstname);
  const [lastname, setLastName] = useState(team?.lastname);
  const [country, setCountry] = useState(team?.country);
  const [city, setCity] = useState(team?.city);
  const [address, setAddress] = useState(team?.address);
  const [thumbnail, setThumbnail] = useState(team?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState(team?.image_cdn_id);
  const [phonenumber, setPhoneNumber] = useState(team?.phone_number);
  const [email, setEmail] = useState(team?.email_address);
  const [role, setRole] = useState(team?.role);
  const [teamStatus, setTeamStatus] = useState(team?.status);
  const [description, setDescription] = useState(team?.description);

  const teamRoutes = "/team";
  const hasPhoto = !!thumbnailId;

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
  };

  const responses = (isError, _status, _message) => {
    setError(isError);
    setStatus(_status);
    setErrorMessage(_message);
  };

  const updateOneTeamHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      return responses(true, 400, "Image Required");
    } else {
      sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      email_address: email,
      firstname: firstname,
      lastname: lastname,
      password: "000000",
      country: country,
      city: city,
      address: address,
      phone_number: phonenumber,
      role: role,
      description: description,
      status: teamStatus,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "...",
    };

    const response = await updateTeam(team_id, data);
    if (response) {
      if (response.data.status === codeOk) {
        updateTeamState({ team_id, ...data });
        cleanFields();
        return responses(true, response.data.status, response.data.message);
      } else {
        setLoading(false);
        return responses(true, response.data.status, response.data.message);
      }
    } else {
      setLoading(false);
      return responses(true, 400, errorMessage);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 rounded-b-xl">
      <div className="mx-auto max-w-6xl">
        <GoBack props={{ route: teamRoutes }} />

        <div className="relative mt-3 mb-5">
          <div className="pointer-events-none absolute -inset-x-6 -top-6 h-24 rounded-3xl bg-gradient-to-r from-emerald-400/15 via-amber-400/15 to-fuchsia-400/15 blur-2xl -z-10" />
          <div className="relative rounded-2xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur">
            <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-base font-semibold tracking-tight text-gray-900">Edit Team Member</h1>
                <p className="mt-0.5 text-xs text-gray-600">Update identity, contact and status.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusChip label="Profile Photo" ok={hasPhoto} />
                <StatusChip label={teamStatus === 1 ? "Visible" : "Invisible"} ok={teamStatus === 1} />
              </div>
            </div>
          </div>
        </div>

        <Message
          props={{
            error,
            success,
            message,
            loading,
            status,
            onClick: () => {
              setError(false);
              setSuccess(false);
              setErrorMessage("");
            },
          }}
        />

        <form onSubmit={updateOneTeamHandler} className="space-y-5">
          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 md:p-5">
            <div className="mb-3 flex items-center justify-between">
              <Title props={{ title: "Profile Photo" }} />
              <span className="text-[11px] text-gray-500">JPEG/PNG — square recommended</span>
            </div>
            <div className="rounded-xl border border-dashed border-gray-200 p-3">
              <ImageKitUpload
                thumbnail={thumbnail}
                thumbnailId={thumbnailId}
                setThumbnail={setThumbnail}
                setThumbnailId={setThumbnailId}
                folder={FOLDERS.TEAM}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 md:p-5">
            <div className="mb-3">
              <Title props={{ title: "Member Details" }} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <InputField
                props={{
                  id: "firstname",
                  title: "First Name",
                  type: "text",
                  value: firstname ? firstname : "",
                  onChange: (e) => {
                    setFirstName(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "lastname",
                  title: "Last Name",
                  type: "text",
                  value: lastname ? lastname : "",
                  onChange: (e) => {
                    setLastName(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "role",
                  title: "Role",
                  type: "text",
                  value: role ? role : "",
                  onChange: (e) => {
                    setRole(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "country",
                  title: "Country",
                  type: "text",
                  value: country ? country : "",
                  onChange: (e) => {
                    setCountry(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "city",
                  title: "City",
                  type: "text",
                  value: city ? city : "",
                  onChange: (e) => {
                    setCity(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
              <InputField
                props={{
                  id: "address",
                  title: "Address",
                  type: "text",
                  value: address ? address : "",
                  onChange: (e) => {
                    setAddress(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 md:p-5">
            <div className="mb-3">
              <Title props={{ title: "Contact Info" }} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="flex items-center">
                <PhoneInput setPhone={setPhoneNumber} phone={phonenumber} required={true} />
              </div>
              <InputField
                props={{
                  id: "email",
                  title: "Email Address",
                  type: "email",
                  required: true,
                  value: email ? email : "",
                  onChange: (e) => {
                    setEmail(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4 md:p-5">
            <div className="mb-3">
              <Title props={{ title: "Settings" }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <Select
                props={{
                  id: "event_status",
                  title: "Status",
                  option: ["visible", "invisible"],
                  onChange: (e) => {
                    if (e.target.selectedIndex === 0) setTeamStatus(1);
                    else setTeamStatus(0);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>
            <div className="mt-4">
              <TextArea
                props={{
                  id: "description",
                  title: "Description",
                  type: "text",
                  value: description ? description : "",
                  onChange: (e) => {
                    setDescription(e.target.value);
                    setError(null);
                    setSuccess(null);
                  },
                }}
              />
            </div>
          </section>

          <div className="flex items-center justify-end">
            <div className="w-full rounded-xl bg-white shadow-sm ring-1 ring-gray-200 p-2">
              <ActionButton
                props={{
                  title: "Save Changes",
                  error: error,
                  success: success,
                  message: message,
                  loading: loading,
                  status: status,
                  onClick: () => {
                    setError(false);
                    setSuccess(false);
                    setErrorMessage("");
                  },
                }}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Edit;

const StatusChip = ({ label, ok }) => (
  <span
    className={[
      "inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1",
      ok ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-gray-50 text-gray-600 ring-gray-200",
    ].join(" ")}
  >
    <span className={["h-1.5 w-1.5 rounded-full", ok ? "bg-emerald-500" : "bg-gray-300"].join(" ")} />
    {label}
  </span>
);
